import { NgStyle } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CartStore } from '@apps/mtjr/customers/app/cart/cart.store';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonLabel,
  IonList,
  IonSpinner,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { ApiService } from '@libs/core/base-config';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { getSharedCart } from '@libs/mtjr/shared/generated/entities/shared-cart/service/shared-cart.service';
import { Dtos } from '@libs/mtjr/shared/generated/types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'mac-shared-cart',
  standalone: true,
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonBackButton,
    IonContent,
    IonCard,
    IonList,
    IonText,
    IonLabel,
    IonButton,
    IonSpinner,
    VxCurrencyPipe,
    VxDynamicImageComponent,
    TranslatePipe,
    NgStyle,
  ],
  templateUrl: './shared-cart.page.html',
})
export class SharedCartPage implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiService = inject(ApiService);
  private cartStore = inject(CartStore);
  private appAlerts = inject(AppAlerts);
  private translate = inject(TranslateService);

  sharedCartItems = signal<Dtos.GetSharedCartItem[]>([]);
  isLoading = signal(true);
  error = signal<string | null>(null);
  isReplacing = signal(false);

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set(this.translate.instant('SHARED_CART.INVALID_LINK'));
      this.isLoading.set(false);
      return;
    }

    this.loadSharedCart(id);
  }

  private loadSharedCart(id: string) {
    getSharedCart(this.apiService, { id }).subscribe({
      next: (response) => {
        this.sharedCartItems.set(response.items);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading shared cart:', err);
        this.error.set(this.translate.instant('SHARED_CART.LOAD_ERROR'));
        this.isLoading.set(false);
      },
    });
  }

  async replaceCart() {
    if (this.isReplacing()) return;

    const confirm = await this.appAlerts.yesNoAlertTR({
      header: 'SHARED_CART.REPLACE_CART',
      message: 'SHARED_CART.REPLACE_CART_CONFIRM',
      yesCaption: 'COMMON.YES',
      noCaption: 'COMMON.NO',
    });

    if (!confirm) return;

    this.isReplacing.set(true);
    try {
      // Clear current cart
      this.cartStore.reset();

      // Add all items from shared cart
      for (const item of this.sharedCartItems()) {
        const variation = item.variantId ? item.product.variations?.find((v) => v.id === item.variantId) : undefined;

        this.cartStore.addItem({
          product: item.product,
          variation: variation,
          quantity: item.quantity,
        });
      }

      await this.appAlerts.successSnackbarTR('SHARED_CART.CART_REPLACED');
      this.router.navigate(['/cart']);
    } catch (error) {
      console.error('Error replacing cart:', error);
      await this.appAlerts.dangerSnackbarTR('SHARED_CART.REPLACE_ERROR');
    } finally {
      this.isReplacing.set(false);
    }
  }

  getItemPrice(item: Dtos.GetSharedCartItem): number {
    const variation = item.variantId ? item.product.variations?.find((v) => v.id === item.variantId) : undefined;
    return (variation?.price || item.product.price || 0) - (item.product.discount || variation?.discount || 0);
  }

  getItemVariation(item: Dtos.GetSharedCartItem) {
    return item.variantId ? item.product.variations?.find((v) => v.id === item.variantId) : undefined;
  }
}
